/**
 * Smart Parser Hook
 * Regex-first parsing with optional AI enhancement.
 * Tracks corrections (deletions, AI re-extracts) and sends feedback to backend.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../../utils/api';
import { parseSmartInput } from '../CorrectionPanel/SmartInput/InputParser';

/**
 * Hook for smart parsing functionality
 */
export const useSmartParser = (documentType = 'flight', referenceDate = new Date()) => {
  const [parseResult, setParseResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fieldProcessing, setFieldProcessing] = useState(null); // field name being AI-processed
  const [error, setError] = useState(null);

  // Track corrections for feedback
  const correctionsRef = useRef([]);
  const originalRegexResultRef = useRef(null);
  const inputTextRef = useRef('');

  /**
   * Parse with local regex (fast, free)
   */
  const parse = useCallback((input) => {
    if (!input || typeof input !== 'string' || !input.trim()) {
      setParseResult(null);
      setError(null);
      return null;
    }

    const trimmed = input.trim();
    inputTextRef.current = trimmed;
    correctionsRef.current = [];

    try {
      const result = parseSmartInput(trimmed, documentType, referenceDate);
      originalRegexResultRef.current = result.success ? { ...result.data } : {};
      setParseResult(result);
      setError(null);
      return result;
    } catch (err) {
      console.error('Regex parse error:', err);
      setError(err.message);
      setParseResult(null);
      return null;
    }
  }, [documentType, referenceDate]);

  /**
   * Parse with AI (full re-parse via backend)
   */
  const parseWithAI = useCallback(async (input) => {
    const trimmed = (input || inputTextRef.current || '').trim();
    if (!trimmed) return null;

    setIsProcessing(true);
    setError(null);
    inputTextRef.current = trimmed;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/smart-parse`,
        { text: trimmed, document_type: documentType },
        { headers, timeout: 15000 }
      );

      if (response.data.success && Object.keys(response.data.data).length > 0) {
        const aiResult = {
          success: true,
          data: response.data.data,
          confidence: response.data.confidence || {},
          format: 'ai',
          errors: []
        };

        // Track what changed from regex if we had regex results
        if (originalRegexResultRef.current) {
          const regexData = originalRegexResultRef.current;
          for (const [field, aiValue] of Object.entries(aiResult.data)) {
            const regexValue = regexData[field];
            if (regexValue !== undefined && regexValue !== aiValue) {
              correctionsRef.current.push({
                field,
                regex_value: regexValue,
                corrected_value: aiValue,
                correction_type: 'ai_corrected'
              });
            }
          }
        }

        setParseResult(aiResult);
        setIsProcessing(false);
        return aiResult;
      }

      setError('AI returned no results.');
      setIsProcessing(false);
      return null;
    } catch (err) {
      console.error('AI parse error:', err);
      setError(err.response?.data?.error || err.message);
      setIsProcessing(false);
      return null;
    }
  }, [documentType]);

  /**
   * Re-extract a single field with AI
   */
  const parseFieldWithAI = useCallback(async (fieldName) => {
    const text = inputTextRef.current;
    if (!text) return null;

    setFieldProcessing(fieldName);

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/smart-parse-field`,
        { text, document_type: documentType, field_name: fieldName },
        { headers, timeout: 10000 }
      );

      if (response.data.success && response.data.value != null) {
        const oldValue = parseResult?.data?.[fieldName];

        // Track correction
        correctionsRef.current.push({
          field: fieldName,
          regex_value: oldValue ?? null,
          corrected_value: response.data.value,
          correction_type: 'ai_corrected'
        });

        // Update the field in parseResult
        setParseResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            data: { ...prev.data, [fieldName]: response.data.value },
            confidence: { ...prev.confidence, [fieldName]: response.data.confidence || 0.9 }
          };
        });

        setFieldProcessing(null);
        return response.data.value;
      }

      setFieldProcessing(null);
      return null;
    } catch (err) {
      console.error(`AI field parse error for ${fieldName}:`, err);
      setFieldProcessing(null);
      return null;
    }
  }, [documentType, parseResult]);

  /**
   * Remove a field from the parse result
   */
  const removeField = useCallback((fieldName) => {
    const oldValue = parseResult?.data?.[fieldName];

    // Track deletion
    correctionsRef.current.push({
      field: fieldName,
      regex_value: oldValue ?? null,
      corrected_value: null,
      correction_type: 'deleted'
    });

    setParseResult(prev => {
      if (!prev) return prev;
      const newData = { ...prev.data };
      delete newData[fieldName];
      const newConfidence = { ...prev.confidence };
      delete newConfidence[fieldName];
      return {
        ...prev,
        data: newData,
        confidence: newConfidence,
        success: Object.keys(newData).length > 0
      };
    });
  }, [parseResult]);

  /**
   * Send feedback to backend (fire-and-forget)
   */
  const sendFeedback = useCallback(() => {
    if (correctionsRef.current.length === 0) return;
    if (!originalRegexResultRef.current) return;

    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    axios.post(
      `${apiBase}/admin/parsing-qa/smart-input-feedback`,
      {
        document_type: documentType,
        input_text: inputTextRef.current,
        regex_results: originalRegexResultRef.current,
        corrections: correctionsRef.current
      },
      { headers }
    ).catch(err => {
      console.warn('Failed to send smart input feedback:', err.message);
    });

    // Reset after sending
    correctionsRef.current = [];
  }, [documentType]);

  /**
   * Clear parse result
   */
  const clearResult = useCallback(() => {
    setParseResult(null);
    setError(null);
    correctionsRef.current = [];
    originalRegexResultRef.current = null;
  }, []);

  /**
   * Get fields that were successfully extracted
   */
  const extractedFields = useMemo(() => {
    if (!parseResult || !parseResult.success) return [];

    return Object.entries(parseResult.data)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        field: key,
        value,
        confidence: parseResult.confidence[key] || 0.5
      }));
  }, [parseResult]);

  /**
   * Get confidence level for display
   */
  const getConfidenceLevel = useCallback((confidence) => {
    if (confidence >= 0.9) return 'high';
    if (confidence >= 0.7) return 'medium';
    return 'low';
  }, []);

  const hasData = useMemo(() => {
    return parseResult?.success && Object.keys(parseResult.data).length > 0;
  }, [parseResult]);

  const detectedFormat = useMemo(() => {
    return parseResult?.format || null;
  }, [parseResult]);

  const hasCorrections = correctionsRef.current.length > 0;

  return {
    // State
    parseResult,
    isProcessing,
    fieldProcessing,
    error,
    extractedFields,
    hasData,
    detectedFormat,
    hasCorrections,

    // Actions
    parse,
    parseWithAI,
    parseFieldWithAI,
    removeField,
    sendFeedback,
    clearResult,

    // Utilities
    getConfidenceLevel
  };
};

export default useSmartParser;
